import React, { useMemo } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const PerformanceCharts = ({ players, coaches, goalkeepers, filterRole }) => {
  const positionDistribution = useMemo(() => {
    if (filterRole !== 'all' && filterRole !== 'player') return [];
    const positions = {};
    players?.forEach(p => {
      const pos = p?.position || 'Unknown';
      positions[pos] = (positions?.[pos] || 0) + 1;
    });
    return Object.entries(positions)?.map(([name, value]) => ({ name, value }));
  }, [players, filterRole]);

  const performanceComparison = useMemo(() => {
    const data = [];
    if (filterRole === 'all' || filterRole === 'player') {
      const avgGoals = players?.length > 0 ? players?.reduce((sum, p) => sum + (p?.goalsScored || 0), 0) / players?.length : 0;
      const avgAssists = players?.length > 0 ? players?.reduce((sum, p) => sum + (p?.assists || 0), 0) / players?.length : 0;
      data?.push({ category: 'Players', goals: avgGoals?.toFixed(1), assists: avgAssists?.toFixed(1) });
    }
    if (filterRole === 'all' || filterRole === 'goalkeeper') {
      const avgCleanSheets = goalkeepers?.length > 0 ? goalkeepers?.reduce((sum, g) => sum + (g?.cleanSheets || 0), 0) / goalkeepers?.length : 0;
      data?.push({ category: 'Goalkeepers', cleanSheets: avgCleanSheets?.toFixed(1) });
    }
    return data;
  }, [players, goalkeepers, filterRole]);

  const coachPerformance = useMemo(() => {
    if (filterRole !== 'all' && filterRole !== 'coach') return [];
    return coaches?.slice(0, 10)?.map(c => ({
      name: c?.fullName?.split(' ')?.[0] || 'Unknown',
      wins: c?.gamesWon || 0,
      draws: c?.gamesDrawn || 0,
      losses: c?.gamesLost || 0
    }));
  }, [coaches, filterRole]);

  const transferAvailability = useMemo(() => {
    const available = players?.filter(p => p?.availableForTransfer)?.length + goalkeepers?.filter(g => g?.availableForTransfer)?.length;
    const notAvailable = players?.filter(p => !p?.availableForTransfer)?.length + goalkeepers?.filter(g => !g?.availableForTransfer)?.length;
    return [
      { name: 'Available', value: available },
      { name: 'Not Available', value: notAvailable }
    ];
  }, [players, goalkeepers]);

  const COLORS = ['var(--color-primary)', 'var(--color-secondary)', 'var(--color-accent)', 'var(--color-success)', 'var(--color-warning)'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs text-muted-foreground">
              <span style={{ color: entry?.color }}>{entry?.name}: </span>
              <span className="font-semibold">{entry?.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Position Distribution */}
      {(filterRole === 'all' || filterRole === 'player') && positionDistribution?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="PieChart" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">Player Position Distribution</h3>
              <p className="text-sm text-muted-foreground">Breakdown by playing position</p>
            </div>
          </div>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={positionDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={100}
                  fill="var(--color-primary)"
                  dataKey="value"
                >
                  {positionDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Performance Comparison */}
      {performanceComparison?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-secondary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">Average Performance Metrics</h3>
              <p className="text-sm text-muted-foreground">Comparative analysis across roles</p>
            </div>
          </div>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="category" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                {performanceComparison?.[0]?.goals && <Bar dataKey="goals" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />}
                {performanceComparison?.[0]?.assists && <Bar dataKey="assists" fill="var(--color-secondary)" radius={[8, 8, 0, 0]} />}
                {performanceComparison?.[0]?.cleanSheets && <Bar dataKey="cleanSheets" fill="var(--color-success)" radius={[8, 8, 0, 0]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Coach Performance */}
      {(filterRole === 'all' || filterRole === 'coach') && coachPerformance?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-accent" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">Coach Win/Draw/Loss Records</h3>
              <p className="text-sm text-muted-foreground">Performance breakdown by coach</p>
            </div>
          </div>
          <div className="w-full h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={coachPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="wins" fill="var(--color-success)" radius={[8, 8, 0, 0]} />
                <Bar dataKey="draws" fill="var(--color-warning)" radius={[8, 8, 0, 0]} />
                <Bar dataKey="losses" fill="var(--color-error)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Transfer Availability */}
      {transferAvailability?.some(item => item?.value > 0) && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <Icon name="Target" size={20} className="text-success" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">Transfer Availability Status</h3>
              <p className="text-sm text-muted-foreground">Current market availability</p>
            </div>
          </div>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={transferAvailability}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="var(--color-primary)"
                  dataKey="value"
                >
                  <Cell fill="var(--color-success)" />
                  <Cell fill="var(--color-muted)" />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Empty State */}
      {positionDistribution?.length === 0 && performanceComparison?.length === 0 && coachPerformance?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="BarChart3" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Analytics Data</h3>
          <p className="text-muted-foreground">No performance data available for visualization.</p>
        </div>
      )}
    </div>
  );
};

export default PerformanceCharts;